"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import type { SanityImage } from "@/sanity/lib/types";
import { urlForImage } from "@/sanity/lib/image";

type Props = { images: SanityImage[] };

export default function ShopGallery({ images }: Props) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent((c) => (c + 1) % images.length), [images.length]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [paused, next, images.length]);

  const urls = images.map((img) =>
    img.asset ? urlForImage(img)?.width(1200).height(700).fit("crop").url() ?? null : null
  );

  return (
    <section className="mt-12 border-t border-slate-100 py-12 px-4">
      <div className="mx-auto max-w-4xl">
        <p className="mb-8 text-center text-xs uppercase tracking-[0.4em] text-slate-500">Gallery</p>
        <div
          className="relative overflow-hidden rounded-sm bg-slate-100"
          style={{ aspectRatio: "16/9" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {urls.map((url, i) =>
            url ? (
              <div
                key={i}
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: i === current ? 1 : 0 }}
              >
                <Image
                  src={url}
                  alt={images[i].alt ?? "Gallery photo"}
                  fill
                  className="object-cover"
                  sizes="(max-width: 896px) 100vw, 896px"
                  priority={i === 0}
                />
              </div>
            ) : null
          )}

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-sm bg-white/70 p-2 text-slate-700 backdrop-blur transition hover:bg-white"
                aria-label="Previous photo"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm bg-white/70 p-2 text-slate-700 backdrop-blur transition hover:bg-white"
                aria-label="Next photo"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCurrent(i)}
                    className={`h-1.5 rounded-full transition-all ${i === current ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
                    aria-label={`Go to photo ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
